import type { DropItem, DropItemNft } from "./types";

/**
 * HOW MUCH OF A DROP ITEM IS LEFT — read from what the backend states.
 *
 * An item offers `quantity` copies; every copy minted becomes an NFT and is
 * listed in the item's `nfts` (with its contract and token id). So:
 *  - minted    = the number of NFTs the item lists;
 *  - remaining = quantity − minted;
 *  - sold out  = nothing remaining — the mint is closed for that item.
 * When the payload omits `nfts` (an older response), minted is UNKNOWN (null)
 * and the item is not treated as sold out: the old behaviour, nothing invented.
 */
export type ItemMintState = {
  quantity: number | null;
  minted: number | null;
  remaining: number | null;
  soldOut: boolean;
  nfts: DropItemNft[];
};

const positive = (v: unknown) => (typeof v === "number" && Number.isFinite(v) && v > 0 ? v : null);

export function itemQuantity(item: DropItem): number | null {
  return positive(item.quantity) ?? positive(item.meta_data?.quantity) ?? positive(item.meta_data?.count_nft);
}

export function itemMintState(item: DropItem): ItemMintState {
  const quantity = itemQuantity(item);
  // Only an NFT that can be addressed (contract + token) counts — it is what the link opens.
  const nfts = Array.isArray(item.nfts)
    ? item.nfts.filter((n): n is DropItemNft => Boolean(n && n.contract_address && n.token_id !== null && n.token_id !== undefined && n.token_id !== ""))
    : null;
  const minted = nfts ? nfts.length : null;
  const remaining = quantity !== null && minted !== null ? Math.max(0, quantity - minted) : null;
  return { quantity, minted, remaining, soldOut: remaining === 0, nfts: nfts ?? [] };
}

/**
 * An item with even one minted copy is on chain as an NFT: its name, picture,
 * price and supply are fixed from then on, so the owner can no longer edit or
 * delete it.
 */
export function isItemMinted(item: DropItem): boolean {
  return (itemMintState(item).minted ?? 0) > 0;
}

/** The single NFT page of one minted copy. */
export function mintedNftHref(lang: string, networkName: string | undefined, nft: DropItemNft): string {
  return `/${lang}/assets/${networkName || "ethereum"}/${nft.contract_address}/${nft.token_id}`;
}

/**
 * The drop as a whole, from its items: minted is the sum only when EVERY item
 * states its NFTs and its quantity (the same rule as the supply in itemFacts);
 * sold out when every item is.
 */
export function dropMintTotals(items: DropItem[]): { minted: number | null; soldOut: boolean } {
  if (items.length === 0) return { minted: null, soldOut: false };
  const states = items.map(itemMintState);
  const known = states.every((s) => s.minted !== null && s.quantity !== null);
  return {
    minted: known ? states.reduce((a, s) => a + (s.minted as number), 0) : null,
    soldOut: known && states.every((s) => s.soldOut),
  };
}
